import { Typography } from '@/components/common';
import { QuestionType } from '@/interfaces';
import * as React from 'react';
import { AnswerRow } from './AnswerRow';
import { FilterQuestion } from './DocumentFilterPanel';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getQuestionTypeLabel(type: QuestionType): string {
	const map: Record<QuestionType, string> = {
		YES_NO: 'Yes / No Question',
		MULTIPLE: 'Multiple Choice',
		TEXT: 'Short Answer',
		DATE: 'Date',
	};
	return map[type];
}

/** A single question card with selectable answers */
export const FilterQuestionCard: React.FC<{
	question: FilterQuestion;
	questionIndex: number;
	selectedAnswerIds: string[];
	onToggleAnswer: (answerId: string) => void;
}> = ({ question, questionIndex, selectedAnswerIds, onToggleAnswer }) => {
	const isTextType = question.questionType === 'TEXT';
	const isDateType = question.questionType === 'DATE';
	const selectedCount = selectedAnswerIds.length;

	const dateRangeId = isDateType
		? `${question.answers[0]?.text} - ${question.answers[1]?.text}`
		: '';

	return (
		<div
			className={`bg-white border border-grey-100 rounded-2xl p-4 space-y-3 ${
				isTextType ? 'shadow-shadow2 opacity-50' : ''
			}`}
		>
			{/* Card header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="w-6 h-6 rounded-full bg-primary text-white text-base font-bold flex items-center justify-center flex-shrink-0">
						{questionIndex}
					</span>
					<Typography type="semibold-14" className="text-black">
						{getQuestionTypeLabel(question.questionType)}
					</Typography>
				</div>
				{!isTextType && (
					<span className="text-xs text-primary font-semibold bg-[#8039DF1A] rounded-full px-3 py-1">
						{selectedCount} selected
					</span>
				)}
			</div>

			{/* Question text */}
			<div className="bg-grey-5 rounded-lg px-4 py-2.5">
				<Typography type="semibold-16" className="text-opacity-black text-left">
					{question.question}
				</Typography>
			</div>

			{/* Answer options / Placeholder */}
			{isTextType ? (
				<div className="bg-grey-5 rounded-[32px] border border-grey-100 px-4 py-6 h-32">
					<Typography
						type="regular-16"
						className="text-opacity-primary text-left"
					>
						Write your answer here
					</Typography>
				</div>
			) : isDateType ? (
				<AnswerRow
					answer={{ id: dateRangeId, text: dateRangeId }}
					selected={selectedAnswerIds.includes(dateRangeId)}
					questionType={question.questionType}
					questionIndex={0}
					onToggle={() => onToggleAnswer(dateRangeId)}
				/>
			) : (
				<div className="space-y-2">
					{question.answers.map((answer, index) => (
						<AnswerRow
							key={answer.id}
							answer={answer}
							selected={selectedAnswerIds.includes(answer.id)}
							questionType={question.questionType}
							questionIndex={index}
							onToggle={() => onToggleAnswer(answer.id)}
						/>
					))}
				</div>
			)}
		</div>
	);
};
